'use client';
import { useRouter } from 'next/navigation';
import { useState, useEffect, FC, useRef, FormEvent } from 'react';
import { toast } from 'react-toastify';

import { Button, Svg, Text } from '@/components';
import useToggle from '@/hooks/useToggle';
import { http, storage, endpoints, logger } from '@/config';
import { ComponentPropTypes } from './LoginSignupFlow';
import { useCleverTap } from '@/context/clevertapContext';
import { verifyOtpAction, updateGuestUserCartAction } from '@/app/actions';
import { useCart } from '@/context/cartContext';
import SecureIcon from '@/icons/SecureIcon';
import TimerIcon from '@/icons/TimerIcon';
import BackIcon from '@/icons/BackIcon';
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { MENU_LINKS } from '@/constants';

type PropTypes = ComponentPropTypes;

type parsedOtpResponseType = {
  userDetails?: { customer_id: string; store_id: string };
  auth_token?: string;
};

const INITIAL_COUNTDOWN = 30;
const ERROR_MESSAGES = {
  INVALID: 'Invalid one-time password. Try again or generate a new OTP',
  EXPIRED: 'The verification code has expired.',
};

const OtpCode: FC<PropTypes> = ({
  setIndex,
  onClose,
  loginRedirect,
  source,
  data: bookingData,
  dataKey,
  page,
}) => {
  const router: any = useRouter();
  const countDownRef = useRef<any | null>(null);
  const { postLoginCartOperations } = useCart();
  const { pushEvent } = useCleverTap();
  const { value: isSubmitting, toggleValue: setSubmitting } = useToggle();
  const [time, setTime] = useState<number>(INITIAL_COUNTDOWN);
  const [error, setError] = useState<string | null>(null);
  const [value, setValue] = useState('');

  const decreaseCounter = () => setTime(prev => prev && prev - 1);

  useEffect(() => {
    countDownRef.current = setInterval(decreaseCounter, 1000);
    return () => clearInterval(countDownRef.current);
  }, []);

  useEffect(() => {
    if (value.length === 6) {
      onSubmit();
    }
  }, [value]);

  const onResend = async () => {
    try {
      if (isSubmitting) return;

      setSubmitting(true);
      setError(null);
      const { data, response } = await http.post(endpoints.SEND_OTP_ENDPOINT, {
        mobile: storage?.get('mobile_number'),
      });
      if (response?.ok) {
        toast.success(data?.message);
        setValue('');
        setTime(INITIAL_COUNTDOWN);
      } else throw new Error(data?.message);
    } catch (error: any) {
      setTime(INITIAL_COUNTDOWN);
      toast.error(error?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async () => {
    if (isSubmitting || value.length !== 6) return;

    setSubmitting(true);

    try {
      // Verify OTP and parse response
      const otpResponse = await verifyOtpAction(value);
      const parsedOtpResponse = JSON.parse(otpResponse);

      // Handle OTP verification failure
      if (!parsedOtpResponse?.success) {
        pushEvent({
          event: 'Login',
          data: {
            'Register Option': 'OTP',
            'Customer Mobile': `+91${storage.get('mobile_number')}`,
            Status: 'Failure',
          },
        });

        const errorMessage = parsedOtpResponse?.message
          ?.toLowerCase()
          .includes('expired')
          ? ERROR_MESSAGES.EXPIRED
          : ERROR_MESSAGES.INVALID;
        setError(errorMessage);
        return;
      }

      // Clear any existing errors
      setError(null);

      // Store authentication token
      storage.set('token', parsedOtpResponse?.auth_token);

      // Handle existing customer
      if (parsedOtpResponse?.isCustomerExists) {
        // Handle kitchen studio booking
        if (page === 'kitchen-studio') {
          await handleKitchenStudioBooking();
          return;
        }

        // Clean up storage and close modal
        storage.remove('mobile_number');
        onClose();

        toast.success('Logged in successfully');

        // Track successful login
        pushEvent({
          event: 'Login',
          data: {
            'Register Option': 'OTP',
            'Customer Mobile': `+91${storage.get('mobile_number')}`,
            Status: 'Success',
          },
        });

        // Handle cart update and redirect
        if (loginRedirect) {
          await updateGuestCart(parsedOtpResponse);
          router.push(loginRedirect);

          // Run postLoginCartOperations after redirect
          postLoginCartOperations(parsedOtpResponse);
        } else {
          updateGuestCart(parsedOtpResponse);
          postLoginCartOperations(parsedOtpResponse);
        }
      } else {
        // Handle new customer signup
        storage.set('signup_token', parsedOtpResponse?.signup_token);
        setIndex(2);
      }
    } catch (error) {
      toast.error('Error verifying OTP');
      logger.log('Error verifying OTP', error);
      setError(ERROR_MESSAGES.INVALID);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKitchenStudioBooking = async () => {
    try {
      const endpoint =
        dataKey === 'events'
          ? endpoints.CREATE_EVENT
          : endpoints.CREATE_STUDIO_BOOKING;
      const { data, response } = await http.post(endpoint, bookingData);

      if (!response.ok) {
        throw new Error(data?.message);
      }

      if (dataKey === 'events') {
        sessionStorage.setItem('razorpay_order_id', data?.razorpay_order_id);
        sessionStorage.setItem('events_order_id', data?.order_id);
      } else {
        sessionStorage.setItem('booking_code', data?.code);
        sessionStorage.setItem('booking_id', data?.id);
      }

      router.push(`${MENU_LINKS.KITCHEN_STUDIO}/checkout/`);
    } catch (error) {
      pushEvent({
        event: 'Login',
        data: {
          'Register Option': 'OTP',
          'Customer Mobile': `+91${storage.get('mobile_number')}`,
          Status: 'Failure',
        },
      });
      throw error;
    }
  };

  const updateGuestCart = async (parsedOtpResponse: parsedOtpResponseType) => {
    const cartId = storage.get('cart_id');
    if (!cartId) return;

    try {
      const cartResponse = await updateGuestUserCartAction({
        customerId: parsedOtpResponse?.userDetails?.customer_id,
        storeId: parsedOtpResponse?.userDetails?.store_id,
      });
      const parsedCartResponse = cartResponse ? JSON.parse(cartResponse) : {};

      if (parsedCartResponse?.error) {
        toast.error(parsedCartResponse?.error?.message);
      } else {
        storage.remove('cart_id');
        storage.set('quote_id', parsedCartResponse?.quote_id);
      }
    } catch (error) {
      logger.log('Error updating guest cart action:', error);
    }
  };

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (value.length === 6 && !isSubmitting) {
      onSubmit();
    }
  };

  useEffect(() => {
    const data: any = {
      'Page URL': window.location.href,
      'Page Name': document.title,
      Step: 'Enter OTP',
    };
    if (source) {
      data['Source'] = source;
    }
    pushEvent({
      event: 'Login Flow',
      data,
    });
  }, [pushEvent]);

  return (
    <form name='otp-form' className='space-y-6 pb-3' onSubmit={onFormSubmit}>
      <div className='flex flex-col gap-3 text-left'>
        <div className='flex flex-col gap-[6px]'>
          <button type='button' className='pb-4' onClick={() => setIndex(0)}>
            <BackIcon width={20} height={20} fill='#A0383F' />
          </button>
          <div className='flex items-center gap-1'>
            <Svg className='h-5 w-5 stroke-[#4D7225]'>
              <SecureIcon />
            </Svg>
            <Text className='text-xs font-semibold text-[#4D7225] px-0'>
              100% SECURE
            </Text>
          </div>
        </div>
        <Text className='md:text-[28px] text-2xl font-semibold text-[#38373A] text-left px-0 leading-none'>
          We have sent you a one-time password
        </Text>
        <Text className='opacity-50 px-0 text-sm'>
          Please enter the 6-digit OTP sent to +91-
          {storage?.get('mobile_number')} to proceed.
        </Text>
      </div>

      <div className='space-y-2 w-full flex flex-col justify-center'>
        <div className='flex w-full flex-wrap md:flex-nowrap justify-evenly md:px-3'>
          <InputOTP
            maxLength={6}
            pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
            value={value}
            onChange={setValue}
            autoFocus={true}>
            <InputOTPGroup className='gap-x-3'>
              {/* Repeat for each slot */}
              {Array.from({ length: 6 }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  title='x'
                  className='rounded-lg border md:h-[54px] md:w-[58px] text-center text-lg font-medium focus:border-[#38373A]'
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
        {error && value.length === 6 && (
          <Text className='text-[#A0383F] text-xs px-0 text-left mt-1'>
            {error}
          </Text>
        )}
      </div>

      <div aria-label='resend-otp' className='flex items-center gap-1'>
        {time > 0 && (
          <div className='flex items-center gap-1'>
            <Text className='text-xs text-black text-left font-medium px-0 pr-1'>
              Didn't get OTP? Resend OTP in
            </Text>
            <svg
              width='12'
              height='12'
              viewBox='0 0 12 12'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <TimerIcon />
            </svg>
            <Text className='text-xs text-[#A0383F] font-semibold text-left px-0 pl-[2px]'>
              0:{time < 10 ? `0${time}` : time} sec
            </Text>
          </div>
        )}
        {time === 0 && (
          <Button
            className='py-0 text-fig px-0'
            label='Resend OTP'
            variant='link'
            onClick={onResend}
          />
        )}
      </div>

      <Button
        type='submit'
        label={isSubmitting ? 'Please wait....' : 'CONTINUE'}
        disabled={isSubmitting || !value || value.length < 6 || error !== null}
        className={
          isSubmitting || !value || value.length < 6 || error !== null
            ? 'cursor-not-allowed bg-[#dededf] text-[#605f61] hover:bg-[#e2e2e3]'
            : ''
        }
      />
    </form>
  );
};

export default OtpCode;
